import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  EMPTY,
  catchError,
  distinctUntilChanged,
  map,
  mergeMap,
  take,
  tap
} from 'rxjs';
import { ActivatedRoute, Params } from '@angular/router';
import { ToastManager } from '@porsche-design-system/components-angular';
import { environment } from '../environments/environment';
import { TranslocoService } from '@jsverse/transloco';

interface IUnsubscribeParameters {
  id?: string;
  'newsletter-type'?: string;
  importer?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppService {
  private readonly requiredUrlParameters = [
    'Email',
    'Type',
    'ID',
    'Importer'
  ] as const;

  constructor(
    private http: HttpClient,
    private activatedRoute: ActivatedRoute,
    private toast: ToastManager,
    private transloco: TranslocoService
  ) {}

  public isUnsubscribed$ = new BehaviorSubject(false);

  public hasValidUrlParameters$ =
    this.activatedRoute.queryParams.pipe(
      map(queryParams => this.areUrlParametersValid(queryParams)),
      distinctUntilChanged()
    );

  public emailAddress$ = this.activatedRoute.queryParams.pipe(
    map(queryParams => queryParams['Email'])
  );

  public newsletterType$ = this.activatedRoute.queryParams.pipe(
    map(queryParams => queryParams['Type'])
  );

  // Preserve the existing list-unsubscribe behavior.
  public listUnsubscribed$ = this.activatedRoute.queryParams.pipe(
    map(queryParams => queryParams['ListUnsubscribe'])
  );

  public unsubscribeNewsletter() {
    return this.activatedRoute.queryParams.pipe(
      take(1),
      mergeMap(queryParams => {
        // Block the API request if required parameters are invalid.
        if (!this.areUrlParametersValid(queryParams)) {
          this.isUnsubscribed$.next(false);
          this.showUnsubscribeError();
          return EMPTY;
        }

        // Only send the existing business parameters to the backend.
        const unsubscribeParams: IUnsubscribeParameters = {
          id: queryParams['ID'],
          'newsletter-type': queryParams['Type'],
          importer: queryParams['Importer']
        };

        return this.http.post(
          environment.lambdaEndpoint,
          unsubscribeParams
        );
      }),
      tap(val => {
        // Preserve the existing API response handling.
        if (val) {
          this.isUnsubscribed$.next(true);
        } else {
          this.isUnsubscribed$.next(false);
          this.showUnsubscribeError();
        }
      }),
      catchError(() => {
        this.isUnsubscribed$.next(false);
        this.showUnsubscribeError();
        return EMPTY;
      })
    );
  }

  private areUrlParametersValid(queryParams: Params): boolean {
    // Validate required parameters only; ignore additional parameters.
    return this.requiredUrlParameters.every(parameter => {
      const value: unknown = queryParams[parameter];

      return (
        Object.prototype.hasOwnProperty.call(queryParams, parameter) &&
        typeof value === 'string' &&
        value.trim().length > 0
      );
    });
  }

  public areParamsDefined(
    params: IUnsubscribeParameters
  ): boolean {
    return [
      params.id,
      params['newsletter-type'],
      params.importer
    ].every(
      value =>
        typeof value === 'string' &&
        value.trim().length > 0
    );
  }

  private showUnsubscribeError(): void {
    this.toast.addMessage({
      text: this.transloco.translate(
        'unsubscribe.error.toast_message'
      ),
      state: 'info'
    });
  }
}